import { useState } from "react";
import { LegalLinks } from "@/components/LegalLinks";
import { Badge, Button, Card, ExternalLink, Muted, Segmented } from "@/components/ui";
import { APP_VERSION } from "@/lib/config";
import { TRANSACTION_LABEL, VERIFICATION_LABEL, VERIFICATION_TONE, shortDate } from "@/lib/format";
import { promptInstall, usePwa } from "@/lib/pwa";
import { clearSaved } from "@/lib/saved";
import { useFeeData } from "@/state/fee-data";
import { useTheme } from "@/theme/ThemeProvider";

export default function SettingsPage() {
  const { preference, setPreference } = useTheme();
  const { canInstall, installed } = usePwa();
  const { schedules, origin, fetchedAt, refreshing, lastError, refresh } = useFeeData();
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearNote, setClearNote] = useState<string | null>(null);
  const [openProfile, setOpenProfile] = useState<string | null>(null);

  const onClear = () => {
    try {
      clearSaved();
      setClearNote("All saved calculations were deleted.");
    } catch (err) {
      setClearNote(err instanceof Error ? err.message : String(err));
    }
    setConfirmClear(false);
  };

  return (
    <>
      <h1 className="page-title">Settings</h1>

      <Card>
        <h2 className="heading">Appearance</h2>
        <Segmented
          label="Color theme"
          value={preference}
          onChange={setPreference}
          options={[
            { value: "light", label: "Light" },
            { value: "dark", label: "Dark" },
            { value: "system", label: "System" },
          ]}
        />
        <Muted>{preference === "system" ? "Follows your device's light or dark setting." : `Always ${preference}.`}</Muted>
      </Card>

      <Card>
        <h2 className="heading">Install app</h2>
        {installed ? (
          <Muted>NetKeep is installed on this device.</Muted>
        ) : canInstall ? (
          <>
            <Muted>Add NetKeep to your home screen or desktop. It opens in its own window and works offline.</Muted>
            <Button title="Install NetKeep" onClick={() => void promptInstall()} />
          </>
        ) : (
          <>
            <Muted>Add NetKeep to your home screen or desktop. It opens in its own window and works offline.</Muted>
            <Muted>iPhone or iPad: in Safari, tap Share, then Add to Home Screen.</Muted>
            <Muted>Android: open the browser menu and tap Install app or Add to Home screen.</Muted>
            <Muted>Computer: in Chrome or Edge, use the install button in the address bar.</Muted>
          </>
        )}
      </Card>

      <Card>
        <h2 className="heading">Fee data</h2>
        <Muted>
          {origin === "network"
            ? `Downloaded ${shortDate(fetchedAt)} from the NetKeep server.`
            : origin === "cache"
              ? `Offline. Using fee data saved on ${shortDate(fetchedAt)}.`
              : "Offline. Using fee data bundled with this version of the app."}
        </Muted>
        {lastError ? (
          <p className="text-warning" role="status">
            {lastError}
          </p>
        ) : null}
        <Button title="Check for fee updates" onClick={() => void refresh()} loading={refreshing} disabled={refreshing} />
      </Card>

      <Card>
        <h2 className="heading">Fee profiles</h2>
        <div>
          {schedules.map((s) => {
            const open = openProfile === s.id;
            const detailsId = `profile-${s.id}`;
            return (
              <div key={s.id} className="profile">
                <button
                  type="button"
                  className="profile__toggle"
                  onClick={() => setOpenProfile(open ? null : s.id)}
                  aria-expanded={open}
                  aria-controls={detailsId}
                >
                  <span className="grow">
                    <span className="item__title block">
                      {s.platform.name}
                    </span>
                    <span className="muted">
                      {s.paymentMethod.name} · {TRANSACTION_LABEL[s.transactionType]}
                    </span>
                  </span>
                  <Badge label={VERIFICATION_LABEL[s.verificationStatus]} tone={VERIFICATION_TONE[s.verificationStatus]} />
                </button>
                {open ? (
                  <div id={detailsId} className="stack-sm">
                    <Muted>
                      Version {s.version} · effective {shortDate(s.effectiveFrom)} · last verified{" "}
                      {shortDate(s.lastVerifiedAt)}
                    </Muted>
                    {s.notes ? <Muted className="faint">{s.notes}</Muted> : null}
                    {s.source ? <ExternalLink href={s.source.url}>{s.source.title}</ExternalLink> : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </Card>

      <Card>
        <h2 className="heading">Saved calculations</h2>
        {confirmClear ? (
          <div className="row">
            <Button title="Delete all" variant="danger" flex onClick={onClear} />
            <Button title="Cancel" variant="secondary" flex onClick={() => setConfirmClear(false)} />
          </div>
        ) : (
          <Button title="Delete all saved calculations…" variant="secondary" onClick={() => setConfirmClear(true)} />
        )}
        {clearNote ? <Muted>{clearNote}</Muted> : null}
      </Card>

      <Card>
        <h2 className="heading">About</h2>
        <Muted>NetKeep {APP_VERSION}</Muted>
        <Muted>
          Results are estimates. Platform fees vary by category, seller level, region and promotions, and can change
          without notice. Always confirm with the platform. NetKeep is not affiliated with or endorsed by eBay, Mercari,
          Etsy, PayPal, Meta, or any marketplace. Not tax, legal or financial advice.
        </Muted>
        <LegalLinks />
      </Card>
    </>
  );
}
